import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IAnnotation,
	IGetStudyImageAnnotationThunkArgs,
	IUpdateAnnotationThunkArgs,
} from 'src/features/annotation/domain/types';
import { annotationApi } from 'src/features/annotation/infrastructure/api';
import { IStudyAnnotation } from 'src/features/study';
import { handleThunkError } from 'src/utils/handleThunkError';

export const ANNOTATION_LOADING_KEY = 'annotation';

export const updateAnnotation = createAsyncThunk<
	IAnnotation,
	IUpdateAnnotationThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation/updateAnnotation',
	async (
		{ annotationId, request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationApi.updateAnnotationApi(
				annotationId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update annotation');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getStudyImageAnnotation = createAsyncThunk<
	IStudyAnnotation,
	IGetStudyImageAnnotationThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation/getStudyImageAnnotation',
	async (
		{ studyId, imageId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationApi.getStudyImageAnnotation(
				studyId,
				imageId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to get image annotation',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
